import {
  VIEW_SCHEME_V1,
  type ViewDefinitionV1,
  type ViewDisplay,
} from "@/shared/domain/views";
import type {
  DataTablePreferences,
  DataTableSort,
} from "@/shared/foundation/primitives/Table";

import { sortFromWire, sortToWire } from "../viewSerialization";

/**
 * A complete table layout: every dimension stated. The list's built-in
 * defaults are one; a View's display with its gaps filled is another; what one
 * person has done on top of a View is a third.
 */
export interface TableLayout {
  /** Columns to show, in display order. */
  visibleColumns: string[];
  /** The sort, or `null` for an unsorted table. */
  sort: DataTableSort | null;
  /** Rows per page. */
  pageSize: number;
}

/**
 * A View's display with every gap filled, so "what this View shows" has one
 * answer whether or not the author pinned each dimension. An unstated sort
 * falls back to `querySort` first — the `SORT BY` of a RoboQL View, which the
 * table mirrors when the View is applied — and then to the baseline. A View
 * saved `unsorted` states its sort as none, so nothing is filled in. `null`
 * for a display is the bare table, which is the baseline itself.
 */
export function effectiveLayout(
  display: ViewDisplay | null,
  baseline: TableLayout,
  querySort: DataTableSort | null = null,
): TableLayout {
  if (display === null) {
    return baseline;
  }
  return {
    visibleColumns: display.visible_columns.length
      ? display.visible_columns
      : baseline.visibleColumns,
    sort: display.unsorted
      ? null
      : (sortFromWire(display.sort_by, display.sort_direction) ??
        querySort ??
        baseline.sort),
    pageSize: display.page_size ?? baseline.pageSize,
  };
}

/**
 * `layout` with a remembered draft over it. A draft is a whole layout — it
 * was captured from the table, which reports every dimension — so it stands
 * in for the layout underneath rather than patching it.
 */
export function overlayDraft(
  layout: TableLayout,
  draft: TableLayout | null,
): TableLayout {
  return draft ?? layout;
}

/**
 * The whole layout after the table reported a change. The table sends every
 * dimension it has ever been given, so this is usually a plain conversion;
 * a dimension it has not reported has not moved from `shown`.
 */
export function layoutAfter(
  shown: TableLayout,
  change: DataTablePreferences,
): TableLayout {
  return {
    visibleColumns: change.columns ?? shown.visibleColumns,
    sort: change.sort === undefined ? shown.sort : change.sort,
    pageSize: change.rowsPerPage ?? shown.pageSize,
  };
}

/** Whether two layouts show the same table. Column order counts; it is the point. */
export function sameLayout(a: TableLayout, b: TableLayout): boolean {
  return (
    a.pageSize === b.pageSize &&
    sameSort(a.sort, b.sort) &&
    a.visibleColumns.length === b.visibleColumns.length &&
    a.visibleColumns.every((value, i) => value === b.visibleColumns[i])
  );
}

/**
 * A View definition with its display filled from the baseline, for comparing
 * the live table against what the View shows (rather than what it states).
 */
export function effectiveDefinition(
  definition: ViewDefinitionV1,
  baseline: TableLayout,
  querySort: DataTableSort | null = null,
): ViewDefinitionV1 {
  return {
    ...definition,
    display: toDisplay(
      effectiveLayout(definition.display, baseline, querySort),
    ),
  };
}

/** The bare table as a definition: no filters, the built-in layout. */
export function baselineDefinition(baseline: TableLayout): ViewDefinitionV1 {
  return {
    scheme: VIEW_SCHEME_V1,
    roboql: null,
    filters: null,
    display: toDisplay(baseline),
  };
}

function toDisplay(layout: TableLayout): ViewDisplay {
  return {
    visible_columns: layout.visibleColumns,
    ...sortToWire(layout.sort),
    page_size: layout.pageSize,
  };
}

function sameSort(a: DataTableSort | null, b: DataTableSort | null): boolean {
  if (a === null || b === null) {
    return a === b;
  }
  return a.columnId === b.columnId && a.direction === b.direction;
}
